import { breakpoints } from '@etg/wings';
import { css, cx, etiColors } from '@eti/styles';
import { price as priceUtils, string as stringUtils } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { m as motion } from 'framer-motion';
import { useMediaQuery } from 'usehooks-ts';
import { isEnabled as isModalEnabled } from '../utils/modal';
import { getPriceDivided as getPricePerBound } from '../utils/price';
import { FLEXIBLE_TICKET, MEAL } from '../../products';
import ProductModal from './ProductModal';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const productStyles = productName => {
  switch (productName) {
    case FLEXIBLE_TICKET.name:
      return css`
        width: 100%;

        @media (min-width: ${breakpoints._768}) {
          width: 70%;
        }
      `;
    case MEAL.name:
      return css`
        width: 100%;
      `;
    default:
      return css`
        width: 100%;

        @media (min-width: ${breakpoints._768}) {
          width: fit-content;
        }
      `;
  }
};
const selectionWrapperStyles = (productName, isNewCardDesignEnabled) => css`
  display: grid;
  gap: 8px;
  grid-column: 2;
  grid-row: 3;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto auto;
  margin-inline-start: auto;
  padding: 16px;

  ${productStyles(productName)}

  ${!isNewCardDesignEnabled && css`
    padding: 16px;
    width: 100%;
  `}
`;
const priceWrapperStyles = isNewCardDesignEnabled => css`
  align-items: flex-end;
  align-self: flex-end;
  display: flex;
  flex-basis: auto;
  flex-direction: column;

  ${!isNewCardDesignEnabled && css`
    margin-bottom: 0;
  `}
`;
const priceLabelStyles = css`
  align-self: flex-end;
  color: ${etiColors.grayScale[10]};
  font-size: 0.875rem;
`;
const priceStyles = css`
  font-size: 1rem;
  font-weight: 700;
  text-align: right;

  @media (min-width: ${breakpoints._768}) {
    text-align: right;
  }
`;
const additionalInfoWrapperStyles = css`
  flex: 1 0 auto;
  font-size: 0.875rem;
  margin-right: 0.25rem;
  white-space: nowrap;
`;
const priceAndNoteWrapperStyles = (productName, isNewCardDesignEnabled) => css`
  grid-column: 2 / 3;
  grid-row: 1;

  @media (min-width: ${breakpoints._768}) {
    grid-column: 1;
    grid-row: 2 / 3;
  }

  ${productName === FLEXIBLE_TICKET.name || productName === MEAL.name ? css`
        grid-column: 2;
        grid-row: 1;
      ` : ''}

  ${isNewCardDesignEnabled ? '' : css`
        grid-column: 2;
        grid-row: 1;
      `}
`;
const noteStyles = css`
  color: ${etiColors.black};
  display: block;
  font-size: 0.875rem;
  margin-top: 8px;
`;
const additionalContentStyles = css`
  font-size: 0.875rem;
  grid-column: 1;
  grid-row: 1;
  margin-inline-end: 0.25rem;
  white-space: nowrap;
`;
const selectOneWrapperStyles = css`
  align-self: end;
  grid-column: 1;
  grid-row: 1;

  &::after {
    content: ' *';
  }
`;
const animationProps = (shouldBeShown, isMobile) => ({
  animate: {
    height: shouldBeShown ? 'auto' : 0,
    opacity: shouldBeShown ? 1 : 0
  },
  style: {
    overflow: 'hidden'
  },
  transition: {
    type: 'tween',
    duration: isMobile ? 0.2 : 0
  }
});
const ProductSelection = ({
  additionalBaggage,
  additionalContent,
  buttons,
  confirmation,
  hasInfants,
  infantNoteLabel,
  isConfirmationOpen,
  isFromLabelOnly = false,
  isNewCardDesignEnabled,
  isPersuasionOpen,
  isSelected,
  notSelectedLabel,
  numberOfBounds,
  onConfirmationConfirm,
  onConfirmationDecline,
  onPersuasionConfirm,
  onPersuasionDecline,
  persuasion,
  priceRaw,
  productName,
  shouldShowPrice = true,
  shouldShowPricePerBound = false,
  shouldShowPricePerTraveler,
  title
}) => {
  const {
    t
  } = useTranslation();
  const totalLabel = t('Products.Price.Label.Total');
  const pricePerBoundsLabel = t('Products.Baggages.Price.Label.PerBounds');
  const showPerBound = shouldShowPricePerBound && numberOfBounds && pricePerBoundsLabel;
  const pricePerTravelerLabel = t(`Product.Price.Per.${showPerBound ? 'Bound.And.' : ''}Person`);
  const price = priceUtils.formatPrice(showPerBound ? getPricePerBound(priceRaw, numberOfBounds) : priceRaw);
  const hasPrice = Number.isInteger(priceRaw);
  const priceToDisplay = () => {
    if (shouldShowPricePerTraveler && pricePerTravelerLabel) {
      return stringUtils.insertArgument(pricePerTravelerLabel, price);
    }
    if (showPerBound) {
      return stringUtils.insertArgument(pricePerBoundsLabel, price);
    }
    return price;
  };
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints._768})`);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      className: selectionWrapperStyles(productName, isNewCardDesignEnabled),
      "data-testid": `${productName}-product-selection`,
      children: [productName === FLEXIBLE_TICKET.name && /*#__PURE__*/_jsx("div", {
        className: selectOneWrapperStyles,
        children: t('Product.SelectOneValidation.Message')
      }), /*#__PURE__*/_jsx("div", {
        className: additionalContentStyles,
        children: additionalContent
      }), /*#__PURE__*/_jsxs("div", {
        className: priceAndNoteWrapperStyles(productName, isNewCardDesignEnabled),
        children: [/*#__PURE__*/_jsxs(motion.div, {
          className: priceWrapperStyles(isNewCardDesignEnabled),
          ...animationProps(shouldShowPrice, isMobile),
          children: [/*#__PURE__*/_jsx("div", {
            className: additionalInfoWrapperStyles,
            "data-testid": `${productName}-dimensions`,
            children: additionalBaggage
          }), /*#__PURE__*/_jsx(motion.div, {
            "aria-hidden": !(hasPrice && !shouldShowPricePerTraveler),
            className: cx(priceLabelStyles, 'price__label'),
            ...animationProps(hasPrice && !shouldShowPricePerTraveler, isMobile),
            children: isSelected && !isFromLabelOnly ? totalLabel : notSelectedLabel
          }), /*#__PURE__*/_jsx(motion.div, {
            "aria-hidden": !hasPrice,
            className: cx(priceStyles, 'product-price'),
            "data-testid": `${productName}-price`,
            ...animationProps(hasPrice, isMobile),
            children: priceToDisplay()
          })]
        }), hasInfants && !isNewCardDesignEnabled && /*#__PURE__*/_jsx("span", {
          className: cx(noteStyles, `${productName}-infant-note-label`),
          children: infantNoteLabel
        })]
      }), buttons, isModalEnabled(persuasion) && /*#__PURE__*/_jsx(ProductModal, {
        content: persuasion === null || persuasion === void 0 ? void 0 : persuasion.content,
        isOpen: isPersuasionOpen,
        onConfirm: onPersuasionConfirm,
        onDecline: onPersuasionDecline,
        productName: productName,
        title: title
      }), isModalEnabled(confirmation) && /*#__PURE__*/_jsx(ProductModal, {
        content: confirmation === null || confirmation === void 0 ? void 0 : confirmation.content,
        isOpen: isConfirmationOpen,
        onConfirm: onConfirmationConfirm,
        onDecline: onConfirmationDecline,
        productName: productName,
        title: title
      })]
    })
  });
};
export default ProductSelection;
//# sourceMappingURL=ProductSelection.js.map